import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../../context/ConfigurationContext';
import { PLANS } from '../../constants';
import type { ProfileType } from '../../types';
import {
  AGE_GROUP_QUESTION,
  LIVING_SITUATION_QUESTION,
  HEARING_QUESTION,
  VISION_QUESTION,
} from '../../constants/pre-screening-questions';

const PROFILE_LABELS: Record<ProfileType, { title: string; icon: string; color: string }> = {
  cognitive: { title: 'Memory & Confusion', icon: 'psychology_alt', color: 'text-orange-500' },
  physical: { title: 'Physical', icon: 'touch_app', color: 'text-green-500' },
  visual: { title: 'Visual', icon: 'visibility', color: 'text-blue-500' },
  custom: { title: 'Custom', icon: 'tune', color: 'text-gray-500' },
};

function getPreScreeningLabel(questionOptions: { value: string; label: string }[], value: string): string {
  const option = questionOptions.find(o => o.value === value);
  return option?.label || value;
}

interface ReviewConfirmProps {
  onNext: () => void;
  onBack: () => void;
  onEditStep: (step: string) => void;
}

const ReviewConfirm: FC<ReviewConfirmProps> = ({ onNext, onBack, onEditStep }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');

  const {
    preScreeningAnswers,
    selectedProfile,
    severityLevel,
    selectedPlan,
    selectedLayoutId,
    contacts,
    healthSettings,
  } = useConfiguration();

  const plan = PLANS.find(p => p.id === selectedPlan);
  const profile = selectedProfile ? PROFILE_LABELS[selectedProfile] : null;

  const handleDeploy = () => {
    onNext();
  };

  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-brand-text-light hover:text-brand-text dark:text-slate-400 dark:hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="font-medium">{isFr ? 'Retour' : 'Back'}</span>
            </button>

            <div className="flex items-center gap-2">
              <span className="font-bold text-brand-text dark:text-white">{isFr ? 'R\u00e9viser et confirmer' : 'Review & Confirm'}</span>
            </div>

            <div className="text-sm text-brand-text-muted">
              {isFr ? '\u00c9tape finale' : 'Final Step'}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-brand-bg-warm dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-brand-success rounded-full transition-all duration-500" style={{ width: '100%' }} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-brand-text dark:text-white mb-4">
            {isFr ? 'R\u00e9visez votre configuration' : 'Review your configuration'}
          </h1>
          <p className="text-brand-text-light dark:text-slate-400">
            {isFr ? 'Veuillez v\u00e9rifier tous les param\u00e8tres avant de d\u00e9ployer sur l\u2019appareil' : 'Please review all settings before deploying to the device'}
          </p>
        </div>

        {/* Configuration Summary Sections */}
        <div className="space-y-6">
          {/* Pre-Screening Information */}
          <ReviewSection
            title={isFr ? 'Informations pr\u00e9liminaires' : 'Pre-Screening Information'}
            icon="fact_check"
            onEdit={() => onEditStep('prescreening')}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-brand-text-muted">{isFr ? "Groupe d'\u00e2ge" : 'Age Group'}</p>
                <p className="font-bold text-brand-text dark:text-white mt-1">
                  {getPreScreeningLabel(AGE_GROUP_QUESTION.options, preScreeningAnswers.ageGroup)}
                </p>
              </div>
              <div>
                <p className="text-sm text-brand-text-muted">{isFr ? 'Situation de vie' : 'Living Situation'}</p>
                <p className="font-bold text-brand-text dark:text-white mt-1">
                  {getPreScreeningLabel(LIVING_SITUATION_QUESTION.options, preScreeningAnswers.livingSituation)}
                </p>
              </div>
              <div>
                <p className="text-sm text-brand-text-muted">{isFr ? 'Audition' : 'Hearing'}</p>
                <p className="font-bold text-brand-text dark:text-white mt-1">
                  {getPreScreeningLabel(HEARING_QUESTION.options, preScreeningAnswers.hearing)}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-brand-text-muted">{isFr ? 'Vision' : 'Vision'}</p>
                <p className="font-bold text-brand-text dark:text-white mt-1">
                  {getPreScreeningLabel(VISION_QUESTION.options, preScreeningAnswers.vision)}
                </p>
              </div>
            </div>
          </ReviewSection>

          {/* Profile & Assessment */}
          <ReviewSection
            title={isFr ? 'Profil et \u00e9valuation' : 'Profile & Assessment'}
            icon="psychology_alt"
            onEdit={() => onEditStep('assessment')}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-brand-text-muted">{isFr ? 'Type de profil' : 'Profile Type'}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`material-symbols-outlined ${profile?.color || 'text-brand-text-muted'}`}>
                    {profile?.icon || 'person'}
                  </span>
                  <span className="font-bold text-brand-text dark:text-white">
                    {profile?.title || (isFr ? 'Non d\u00e9fini' : 'Not set')}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-brand-text-muted">{isFr ? 'Niveau de s\u00e9v\u00e9rit\u00e9' : 'Severity Level'}</p>
                <p className="font-bold text-brand-text dark:text-white capitalize mt-1">
                  {severityLevel || (isFr ? 'Non \u00e9valu\u00e9' : 'Not assessed')}
                </p>
              </div>
            </div>
          </ReviewSection>

          {/* Plan */}
          <ReviewSection
            title={isFr ? 'Forfait s\u00e9lectionn\u00e9' : 'Selected Plan'}
            icon="workspace_premium"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xl font-bold text-brand-text dark:text-white capitalize">
                  {plan?.name || (isFr ? 'Aucun forfait s\u00e9lectionn\u00e9' : 'No plan selected')}
                </p>
                <p className="text-sm text-brand-text-muted">
                  {plan?.tagline}
                </p>
              </div>
              {plan && (
                <div className="text-right">
                  <p className="text-2xl font-black text-brand-primary">
                    {plan.priceDisplay}
                  </p>
                  <p className="text-sm text-brand-text-muted">{isFr ? '/mois' : '/month'}</p>
                </div>
              )}
            </div>
            <div className="mt-3 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '18px' }}>card_giftcard</span>
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">{isFr ? 'Essai gratuit de 30 jours inclus \u2014 aucun frais avant le jour 31' : '30-day free trial included \u2014 no charge until day 31'}</p>
            </div>
            {plan && (
              <div className="mt-4 pt-4 border-t border-brand-border dark:border-slate-700">
                <p className="text-sm text-brand-text-muted">
                  {isFr
                    ? `${plan.featureCount.total} fonctions incluses (${plan.featureCount.communication} communication, ${plan.featureCount.safety} s\u00e9curit\u00e9, ${plan.featureCount.health} sant\u00e9)`
                    : `${plan.featureCount.total} features included (${plan.featureCount.communication} communication, ${plan.featureCount.safety} safety, ${plan.featureCount.health} health)`}
                </p>
              </div>
            )}
          </ReviewSection>

          {/* Layout */}
          <ReviewSection
            title={isFr ? "Mod\u00e8le de l'\u00e9cran d'accueil" : 'Home Screen Layout'}
            icon="dashboard"
            onEdit={() => onEditStep('layout')}
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-24 bg-brand-bg-warm dark:bg-slate-700 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-brand-text-muted">smartphone</span>
              </div>
              <div>
                <p className="font-bold text-brand-text dark:text-white">
                  {selectedLayoutId ? (isFr ? 'Grille simplifi\u00e9e' : 'Simplified Grid') : (isFr ? 'Aucun mod\u00e8le s\u00e9lectionn\u00e9' : 'No layout selected')}
                </p>
                <p className="text-sm text-brand-text-muted">
                  {isFr
                    ? `${severityLevel === 'severe' ? '2' : severityLevel === 'moderate' ? '4' : '6'} boutons, optimis\u00e9 pour les besoins ${severityLevel === 'severe' ? 's\u00e9v\u00e8res' : severityLevel === 'moderate' ? 'mod\u00e9r\u00e9s' : 'l\u00e9gers'}`
                    : `${severityLevel === 'severe' ? '2' : severityLevel === 'moderate' ? '4' : '6'} buttons, optimized for ${severityLevel} needs`}
                </p>
              </div>
            </div>
          </ReviewSection>

          {/* Contacts */}
          <ReviewSection
            title={isFr ? "Contacts d'urgence" : 'Emergency Contacts'}
            icon="contacts"
            onEdit={() => onEditStep('contacts')}
          >
            {contacts.length > 0 ? (
              <div className="space-y-3">
                {contacts.map((contact) => (
                  <div key={contact.id} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-bg-warm dark:bg-slate-700 flex items-center justify-center overflow-hidden">
                      {contact.photoUrl ? (
                        <img src={contact.photoUrl} alt={contact.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="material-symbols-outlined text-brand-text-muted">person</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-brand-text dark:text-white">{contact.name}</span>
                        {contact.isEmergency && (
                          <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold rounded-full">
                            {isFr ? 'Urgence' : 'Emergency'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-brand-text-muted">{contact.phone}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-brand-text-muted">{isFr ? 'Aucun contact ajout\u00e9' : 'No contacts added'}</p>
            )}
          </ReviewSection>

          {/* Safety Features */}
          <ReviewSection
            title={isFr ? 'Fonctions de s\u00e9curit\u00e9' : 'Safety Features'}
            icon="shield"
            onEdit={() => onEditStep('features')}
          >
            {/* REMOVED for launch: the safe-zone summary block \u2014 "Safe Zone" /
                "Zone securitaire" with the pinned home coordinates, the radius
                and the monitoring schedule, echoing back what step 5 collected.
                Geofencing is CUT from v1 (Feature 03 spec) and the wizard no
                longer collects a zone, so this block had nothing left to
                summarise; deleted rather than left rendering-never so its copy
                does not ship in the bundle. Restore alongside the step 5 UI in
                src/pages/configuration/SafeZoneSetup.wave1-5.tsx. */}
            <div>
              <p className="font-medium text-brand-text dark:text-white mb-2">{isFr ? 'Fonctions activ\u00e9es automatiquement' : 'Auto-enabled features'}</p>
              <div className="grid grid-cols-2 gap-1.5 text-sm text-brand-text-muted">
                {/* REMOVED for launch: "Phone drop detection" / "D\u00e9tection de chute
                    du t\u00e9l\u00e9phone" \u2014 shown with a check_circle under "Auto-enabled
                    features" while the code path ships disabled everywhere. */}
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '16px' }}>check_circle</span>
                  {isFr ? 'SOS d\u2019urgence' : 'Emergency SOS'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '16px' }}>check_circle</span>
                  {isFr ? "Alertes d'inactivit\u00e9" : 'Inactivity alerts'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '16px' }}>check_circle</span>
                  {isFr ? 'Blocage des appels frauduleux' : 'Scam call blocking'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '16px' }}>check_circle</span>
                  {isFr ? 'Tableau de bord aidant' : 'Caregiver dashboard'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '16px' }}>check_circle</span>
                  {isFr ? 'Partage de localisation GPS' : 'GPS location sharing'}
                </span>
              </div>
            </div>
          </ReviewSection>
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-6 p-4 bg-brand-bg-warm dark:bg-slate-800/50 rounded-xl">
          <p className="text-xs text-brand-text-muted dark:text-slate-400 leading-relaxed">
            {isFr ? (
              <><strong>Important :</strong> AidFone n'est pas un appareil m&eacute;dical. L'alerte de chute du t&eacute;l&eacute;phone surveille le t&eacute;l&eacute;phone, pas la personne. Les temps de r&eacute;ponse des services d'urgence varient selon l'emplacement. Les fonctions n&eacute;cessitent une connexion Internet active. En continuant, vous acceptez nos Conditions d'utilisation et notre Politique de confidentialit&eacute;.</>
            ) : (
              <><strong>Important:</strong> AidFone is not a medical device. Phone Drop Alert monitors the phone, not the person.
            Emergency services response times vary by location. Features require active internet connection.
            By proceeding, you agree to our Terms of Service and Privacy Policy.</>
            )}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
        <div className="max-w-3xl mx-auto flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <button
            onClick={onBack}
            className="w-full sm:w-auto px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors"
          >
            {isFr ? 'Retour' : 'Back'}
          </button>

          <button
            onClick={handleDeploy}
            className="w-full sm:w-auto px-8 py-3 font-bold rounded-xl transition-all bg-brand-primary text-white hover:bg-brand-primary-dark shadow-lg shadow-brand-primary/20"
          >
            {isFr ? 'D\u00e9ployer sur l\u2019appareil' : 'Deploy to Device'}
            <span className="material-symbols-outlined ml-2 align-middle">rocket_launch</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Review Section Component
interface ReviewSectionProps {
  title: string;
  icon: string;
  onEdit?: () => void;
  children: React.ReactNode;
}

const ReviewSection: FC<ReviewSectionProps> = ({ title, icon, onEdit, children }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-brand-border dark:border-slate-700 overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-brand-border dark:border-slate-700">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-brand-primary">{icon}</span>
          <h3 className="font-bold text-brand-text dark:text-white">{title}</h3>
        </div>
        {onEdit && (
          <button
            onClick={onEdit}
            className="flex items-center gap-1 text-sm text-brand-primary hover:text-brand-primary-dark transition-colors"
          >
            <span className="material-symbols-outlined text-lg">edit</span>
            {isFr ? 'Modifier' : 'Edit'}
          </button>
        )}
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
};

export default ReviewConfirm;
